import { Component, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { ValueOfIconUIType } from '../../static/icon/icon-ui-type';

/**
 * Accordion component
 *
 * Provides collapsible content panels for presenting information in a limited amount of space.
 *
 * When _both_ `iconClosed` and `iconOpened` are not set, the the icons can be set with the custom css properties `--accordion-icon-closed` and `--accordion-icon-opened`.
 *
 * @slot - Content of the accordion
 *
 * @example Accordion
 * <web-accordion label="Accordion">
 *  <p>Content of the accordion that will be hidden when collapsed</p>
 * </web-accordion>
 *
 * @example Accordion with icons
 * <web-accordion label="Accordion" icon-closed="bottom-open" icon-opened="chevron-up">
 *  <p>Content of the accordion that will be hidden when collapsed</p>
 * </web-accordion>
 */
@Component({
    tag: 'web-accordion',
    styleUrl: 'accordion.scss',
    shadow: false
})
export class Accordion {
    @Element() rootElement: HTMLWebAccordionElement;

    @Prop() label: string;

    @Prop() iconClosed: ValueOfIconUIType;

    @Prop() iconOpened: ValueOfIconUIType;
    
    @Prop() forceOpen = false;

    @State() toggle = false;

    @Event() accordionToggle: EventEmitter<{ accordion: HTMLWebAccordionElement; toggle: boolean }>;

    private emitToggle = false;

    componentWillLoad() {
        this.toggle = this.rootElement.classList.contains('is-open');
    }

    componentDidRender() {
        if (this.emitToggle) {
            this.emitToggle = false;
            // emit after render so toggle represents the actual post-render dom.
            this.accordionToggle.emit({ accordion: this.rootElement, toggle: this.toggle });
        }
    }

    private toggleComponent = () => {
        if (!this.forceOpen) {
            this.toggle = !this.toggle;
            this.emitToggle = true;
        }
    };

    render() {
        return (
            <Host class={{ 'is-open': this.toggle || this.forceOpen }}>
                <div class="accordion">
                    <button onClick={this.forceOpen ? undefined : this.toggleComponent}>
                        {this.label}
                        {this.iconClosed && !this.toggle && !this.forceOpen && (
                            <web-icon class="accordion-icon-closed" name={this.iconClosed}></web-icon>
                        )}
                        {this.iconOpened && this.toggle && !this.forceOpen && (
                            <web-icon class="accordion-icon-opened" name={this.iconOpened}></web-icon>
                        )}
                    </button>
                    <div class="accordion-content">
                        <slot></slot>
                    </div>
                </div>
            </Host>
        );
    }
}
